/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Own the navigation snapshot stack, panel chrome and transient notice state.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; shared view types; shared transient reset registry
 * @forbidden server entities; roles; permissions; tokens; localStorage; API client
 * @public-api workspace shell and modules
 * @side-effects in-memory UI updates only
 * @tests workspace-behaviour.test.ts
 */

import { create } from 'zustand';
import type { WorkspaceLanguage } from '../lib/i18n';
import type { WorkspaceSection } from '../lib/model';
import { registerWorkspaceTransientReset } from '../lib/transient-reset';

/** `mat` opens one of the class's materials without leaving the class context. */
export type OpenKind = 'stu' | 'minv' | 'ans' | 'rev' | 'prog' | 'iv' | 'mat';

export interface OpenReference {
  readonly k: OpenKind;
  readonly id?: string;
  readonly n?: string;
  readonly stu?: string | null;
  readonly q?: number | null;
}

export type PanelKey = 'list' | 'ref' | 'ivl' | 'ivr' | 'thr';

export const PANEL_DEFAULT_WIDTH: Readonly<Record<PanelKey, number>> = {
  list: 344,
  ref: 380,
  ivl: 210,
  ivr: 300,
  thr: 380,
};

export const PANEL_CSS_VARIABLE: Readonly<Record<PanelKey, string>> = {
  list: '--w-list',
  ref: '--w-ref',
  ivl: '--w-ivl',
  ivr: '--w-ivr',
  thr: '--w-thr',
};

export interface NavigationSnapshot {
  readonly section: WorkspaceSection;
  readonly selection: string | null;
  readonly classTab: string;
  readonly activityTab: string;
  readonly studentTab: string;
  readonly open: OpenReference | null;
}

export interface DisplayOptions {
  readonly view: 'list' | 'board';
  readonly grp: 'st' | 'ci';
  readonly ord: 'upd' | 'new' | 'name' | 'st' | 'tag';
  readonly props: readonly string[];
}

type FilterState = Readonly<Record<string, readonly string[]>>;

interface WorkspaceUiState {
  section: WorkspaceSection;
  selection: string | null;
  open: OpenReference | null;
  classTab: string;
  activityTab: string;
  studentTab: string;
  history: readonly NavigationSnapshot[];
  historyIndex: number;
  mobileMainOpen: boolean;
  language: WorkspaceLanguage;
  listCollapsed: boolean;
  collapsedGroups: Readonly<Record<string, boolean>>;
  panelWidths: Readonly<Record<PanelKey, number>>;
  notice: string | null;
  filters: Readonly<Record<string, FilterState>>;
  display: Readonly<Record<string, DisplayOptions>>;

  go(section: WorkspaceSection, selection: string | null): void;
  pick(selection: string | null, isMobile?: boolean): void;
  navigate(
    section: WorkspaceSection,
    selection: string | null,
    options?: { open?: OpenReference | null; classTab?: string; activityTab?: string },
  ): void;
  openItem(reference: OpenReference): void;
  closeItem(): void;
  setTab(key: 'classTab' | 'activityTab' | 'studentTab', value: string): void;
  back(): void;
  forward(): void;
  closeMobileMain(): void;
  setLanguage(language: WorkspaceLanguage): void;
  toggleList(): void;
  toggleGroup(id: string): void;
  setPanelWidth(key: PanelKey, width: number): void;
  resetPanelWidth(key: PanelKey): void;
  notify(message: string): void;
  clearNotice(): void;
  setFilter(scope: string, key: string, values: readonly string[]): void;
  clearFilter(scope: string, key?: string): void;
  setDisplay(scope: string, options: DisplayOptions): void;
  reset(): void;
}

function snapshotOf(state: WorkspaceUiState): NavigationSnapshot {
  return {
    section: state.section,
    selection: state.selection,
    classTab: state.classTab,
    activityTab: state.activityTab,
    studentTab: state.studentTab,
    open: state.open,
  };
}

function sameSnapshot(a: NavigationSnapshot | undefined, b: NavigationSnapshot): boolean {
  if (!a) return false;
  return (
    a.section === b.section &&
    a.selection === b.selection &&
    a.classTab === b.classTab &&
    a.activityTab === b.activityTab &&
    a.studentTab === b.studentTab &&
    JSON.stringify(a.open ?? null) === JSON.stringify(b.open ?? null)
  );
}

/** Drop the forward stack and append, but only when the snapshot actually changed. */
function pushHistory(
  state: WorkspaceUiState,
  next: Partial<WorkspaceUiState>,
): Partial<WorkspaceUiState> {
  const merged = { ...state, ...next } as WorkspaceUiState;
  const snapshot = snapshotOf(merged);
  if (sameSnapshot(state.history[state.historyIndex], snapshot)) return next;
  const history = [...state.history.slice(0, state.historyIndex + 1), snapshot];
  return { ...next, history, historyIndex: history.length - 1 };
}

function applySnapshot(snapshot: NavigationSnapshot): Partial<WorkspaceUiState> {
  return {
    section: snapshot.section,
    selection: snapshot.selection,
    classTab: snapshot.classTab,
    activityTab: snapshot.activityTab,
    studentTab: snapshot.studentTab,
    open: snapshot.open,
    mobileMainOpen: Boolean(snapshot.selection),
  };
}

const INITIAL_SNAPSHOT: NavigationSnapshot = {
  section: 'room',
  selection: 'c0',
  classTab: 'stu',
  activityTab: 'mat',
  studentTab: 'todo',
  open: null,
};

const INITIAL_STATE = {
  section: INITIAL_SNAPSHOT.section,
  selection: INITIAL_SNAPSHOT.selection,
  open: null as OpenReference | null,
  classTab: INITIAL_SNAPSHOT.classTab,
  activityTab: INITIAL_SNAPSHOT.activityTab,
  studentTab: INITIAL_SNAPSHOT.studentTab,
  history: [INITIAL_SNAPSHOT] as readonly NavigationSnapshot[],
  historyIndex: 0,
  mobileMainOpen: false,
  language: 'zh' as WorkspaceLanguage,
  listCollapsed: false,
  collapsedGroups: {} as Readonly<Record<string, boolean>>,
  panelWidths: PANEL_DEFAULT_WIDTH,
  notice: null as string | null,
  filters: {} as Readonly<Record<string, FilterState>>,
  display: {} as Readonly<Record<string, DisplayOptions>>,
};

export const useWorkspaceUiStore = create<WorkspaceUiState>((set) => ({
  ...INITIAL_STATE,

  go: (section, selection) =>
    set((state) =>
      pushHistory(state, { section, selection, open: null, mobileMainOpen: false, notice: null }),
    ),

  pick: (selection, isMobile = false) =>
    set((state) => pushHistory(state, { selection, mobileMainOpen: isMobile })),

  navigate: (section, selection, options = {}) =>
    set((state) =>
      pushHistory(state, {
        section,
        selection,
        open: options.open ?? null,
        classTab: options.classTab ?? state.classTab,
        activityTab: options.activityTab ?? state.activityTab,
        mobileMainOpen: false,
      }),
    ),

  openItem: (reference) => set((state) => pushHistory(state, { open: reference })),

  closeItem: () => set((state) => pushHistory(state, { open: null })),

  setTab: (key, value) =>
    set((state) => (state[key] === value ? {} : pushHistory(state, { [key]: value }))),

  back: () =>
    set((state) =>
      state.historyIndex > 0
        ? {
            ...applySnapshot(state.history[state.historyIndex - 1]),
            historyIndex: state.historyIndex - 1,
          }
        : {},
    ),

  forward: () =>
    set((state) =>
      state.historyIndex < state.history.length - 1
        ? {
            ...applySnapshot(state.history[state.historyIndex + 1]),
            historyIndex: state.historyIndex + 1,
          }
        : {},
    ),

  closeMobileMain: () => set({ mobileMainOpen: false }),

  setLanguage: (language) => set({ language }),

  toggleList: () => set((state) => ({ listCollapsed: !state.listCollapsed })),

  toggleGroup: (id) =>
    set((state) => ({
      collapsedGroups: { ...state.collapsedGroups, [id]: !state.collapsedGroups[id] },
    })),

  setPanelWidth: (key, width) =>
    set((state) => ({ panelWidths: { ...state.panelWidths, [key]: width } })),

  resetPanelWidth: (key) =>
    set((state) => ({ panelWidths: { ...state.panelWidths, [key]: PANEL_DEFAULT_WIDTH[key] } })),

  notify: (notice) => set({ notice }),
  clearNotice: () => set({ notice: null }),

  setFilter: (scope, key, values) =>
    set((state) => ({
      filters: { ...state.filters, [scope]: { ...(state.filters[scope] ?? {}), [key]: values } },
    })),

  clearFilter: (scope, key) =>
    set((state) => {
      if (!key) return { filters: { ...state.filters, [scope]: {} } };
      const next = { ...(state.filters[scope] ?? {}) };
      delete next[key];
      return { filters: { ...state.filters, [scope]: next } };
    }),

  setDisplay: (scope, options) =>
    set((state) => ({ display: { ...state.display, [scope]: options } })),

  reset: () => set({ ...INITIAL_STATE }),
}));

registerWorkspaceTransientReset('workspace-ui', () => useWorkspaceUiStore.getState().reset());
